import type { ChatThread } from '../types';
import { formatDateLong } from '../utils/formatters';
import { GlobalButton } from './GlobalButton';
import styles from './ChatThreadList.module.css';

export type ChatThreadListProps = {
  threads: ChatThread[];
  activeThreadId: string | null;
  loading: boolean;
  error: string | null;
  creating: boolean;
  onSelectThread: (threadId: string) => void;
  onNewChat: () => void;
};

export function ChatThreadList({
  threads,
  activeThreadId,
  loading,
  error,
  creating,
  onSelectThread,
  onNewChat,
}: ChatThreadListProps) {
  return (
    <aside className={styles.sidebar} aria-label="Chat threads">
      <div className={styles.header}>
        <h2 className={styles.heading}>Chats</h2>
      </div>

      <div className={styles.body}>
        {error ? (
          <p className={styles.error} role="alert">
            {error}
          </p>
        ) : null}

        {loading ? (
          <p className={`${styles.loading} muted`} aria-live="polite">
            Loading chats…
          </p>
        ) : threads.length === 0 ? (
          <div className={styles.emptyState}>
            <h3 className={styles.emptyTitle}>No conversations yet</h3>
            <p className={styles.emptyCopy}>
              Start a chat to ask about your net worth, portfolio, bank activity, or deal pipeline —
              grounded in your Perfina data.
            </p>
            <GlobalButton type="button" autoWidth isLoading={creating} disabled={creating} onClick={onNewChat}>
              New chat
            </GlobalButton>
          </div>
        ) : (
          <ul className={styles.list} role="listbox" aria-label="Conversation list">
            {threads.map((thread) => {
              const isActive = thread.id === activeThreadId;

              return (
                <li key={thread.id} role="none" className={styles.threadItem}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    className={`${styles.threadButton} ${isActive ? styles.threadButtonActive : ''}`}
                    onClick={() => onSelectThread(thread.id)}
                  >
                    <span className={styles.threadTitle}>{thread.title}</span>
                    <span className={styles.threadMeta}>Updated {formatDateLong(thread.updatedAt)}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </aside>
  );
}
